import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	selectProgressCards,
	selectProgressAggregate,
} from "../../services/progress-notifications/selectors";
import { dismissCard } from "../../services/progress-notifications/actions";
import { useAutoFeedActualsSubscription } from "../../services/auto-feed-actuals/useAutoFeedActualsSubscription";
import { useJobRehydration } from "../../hooks/useJobRehydration";
import StackHeader from "./StackHeader";
import CardList from "./CardList";
import Bubble from "./Bubble";
import "./progressNotificationStack.scss";

const COLLAPSE_KEY = "offolio.progressStack.collapsed";

function readCollapsed() {
	try {
		return window.localStorage.getItem(COLLAPSE_KEY) === "1";
	} catch (e) {
		return false;
	}
}

function writeCollapsed(value) {
	try {
		window.localStorage.setItem(COLLAPSE_KEY, value ? "1" : "0");
	} catch (e) {
		// ignore — privacy mode etc.
	}
}

/**
 * Top-level surface mounted once in App.js. Aggregates every long-running async
 * job across the seven Redux slices (import, delete, updateUsages, scenarioCopy,
 * mppBatchImport, aiImport, scenarioDelete) and renders them as a bottom-right
 * stack of cards. Collapses to a floating bubble that keeps animating while
 * the canvas is fully clickable.
 */
export default function ProgressNotificationStack() {
	// Auto-feed actuals is server-initiated: any successful actuals import
	// kicks it off, with no FE call site to colocate the subscription with.
	// Mount it here because the stack is the only always-on, app-level home
	// for long-running job UI — matches the cascade's lifetime exactly.
	useAutoFeedActualsSubscription();
	// Restore in-progress job cards after a page refresh by polling the
	// backend /async-job/{jobId}/status endpoint for each persisted job.
	useJobRehydration();

	const dispatch = useDispatch();
	const cards = useSelector(selectProgressCards);
	const aggregate = useSelector(selectProgressAggregate);
	const [collapsed, setCollapsed] = useState(readCollapsed);

	// "Hide" on a running card removes the CARD, never the job: the Redux
	// entry keeps receiving frames, so the outcome can still surface.
	// Failures un-hide themselves (a hidden job that partially fails must
	// break through), and a hidden job that finishes cleanly is dismissed
	// for real once done.
	const [hiddenIds, setHiddenIds] = useState(() => new Set());
	const hideCard = useCallback((id) => {
		setHiddenIds((prev) => {
			const next = new Set(prev);
			next.add(id);
			return next;
		});
	}, []);
	useEffect(() => {
		if (hiddenIds.size === 0) return;
		for (const card of cards) {
			if (hiddenIds.has(card.id) && card.state === "done") {
				dispatch(dismissCard(card.kind, card.jobId));
			}
		}
	}, [cards, hiddenIds, dispatch]);
	const visibleCards = useMemo(
		() =>
			cards.filter(
				(card) =>
					!hiddenIds.has(card.id) ||
					card.state === "warning" ||
					card.state === "error"
			),
		[cards, hiddenIds]
	);

	// Failures break through a collapsed stack: the bubble's red badge alone
	// is too quiet for something that needs acknowledgment. Expand ONCE per
	// card entering warning/error — re-collapsing afterwards sticks because
	// the id stays in the set (bounded by the session's card count). This is
	// a TRANSIENT expansion: it must not overwrite the user's stored
	// preference, so only the explicit minimize/expand controls persist.
	const alertedCardIdsRef = useRef(new Set());
	useEffect(() => {
		let hasNewAlert = false;
		for (const card of cards) {
			if (
				(card.state === "warning" || card.state === "error") &&
				!alertedCardIdsRef.current.has(card.id)
			) {
				alertedCardIdsRef.current.add(card.id);
				hasNewAlert = true;
			}
		}
		if (hasNewAlert) setCollapsed(false);
	}, [cards]);

	const minimize = useCallback(() => {
		setCollapsed(true);
		writeCollapsed(true);
	}, []);
	const expand = useCallback(() => {
		setCollapsed(false);
		writeCollapsed(false);
		// Expanding is also the way back to cards the user hid.
		setHiddenIds((prev) => (prev.size === 0 ? prev : new Set()));
	}, []);

	// No jobs at all? Render nothing — a bubble counting zero is noise.
	if (cards.length === 0) return null;

	// Every card hidden, but jobs are still running: keep the bubble alive.
	// It is the only signal that background work is in flight ("Hide" promises
	// it keeps running), and clicking it brings the cards back.
	const allHidden = visibleCards.length === 0;

	return (
		<>
			{!allHidden && (
				<section
					className={`pn-stack${collapsed ? " pn-stack--collapsed" : ""}`}
					role="region"
					aria-label="Background tasks"
					aria-live="polite"
					data-testid="progress-stack"
					data-collapsed={collapsed}
				>
					<StackHeader aggregate={aggregate} onMinimize={minimize} />
					<CardList cards={visibleCards} onHide={hideCard} />
				</section>
			)}
			<Bubble
				visible={collapsed || allHidden}
				aggregate={aggregate}
				onExpand={expand}
			/>
		</>
	);
}
